import type { EnemyDefinition, Point } from '../../shared/game/world-types.js';
import type { AttackDefinition, MonsterTrait } from '../../shared/game/combat-types.js';
import { STANDARD_MELEE_REACH, WALK_SPEED, speedForMovementDistance } from '../../shared/game/combat-distances.js';
import { WOLF_VISUAL_HEIGHT, wolfTemplate } from '../characters/rigs/wolf.js';

export type WolfRole = 'wolf' | 'dire-wolf' | 'pack-leader';

const HOWL: MonsterTrait = { id: 'fearsome-howl', dc: 13 };
const bite = (name: string, id: string, count: number, vicious: boolean, strikes?: number): AttackDefinition => ({ id, name, mode: 'melee', count, sides: 8, bonus: 2, stat: null,
  damageType: 'piercing', range: STANDARD_MELEE_REACH, canCrit: true, ...vicious ? { vicious } : {}, ...strikes ? { strikes } : {} });

/** Forest Wolves, MonstersAndMore-v0.1.pdf p. 52. The rig's study profile is never used for gameplay. Size and colour are presentation: `size` scales the study body, `radius` is the authoritative base. */
const BLOCKS: Record<WolfRole, { name: string; level: number; maxHp: number; size: number; radius: number; coat?: number; eyes?: number; attacks: AttackDefinition[]; traits: MonsterTrait[] }> = {
  wolf: { name: 'Wolf', level: .5, maxHp: 18, size: .88, radius: .6, attacks: [bite('Bite', 'wolf-bite', 1, false)], traits: [HOWL, { id: 'pack-hunters' }] },
  'dire-wolf': { name: 'Dire Wolf', level: 2, maxHp: 34, size: 1.15, radius: .75, coat: 0x8f8680, eyes: 0xd0301c,
    attacks: [{ ...bite('Vicious Bite', 'dire-wolf-bite', 2, true) }], traits: [HOWL] },
  'pack-leader': { name: 'Dire Wolf Pack Leader', level: 5, maxHp: 60, size: 1.3, radius: .85, coat: 0x706862, eyes: 0xe23a1c,
    attacks: [bite('Vicious Bite', 'pack-leader-bite', 2, true, 2)], traits: [HOWL, { id: 'protector-of-the-pack', family: 'wolf', reach: 6 }] },
};

export function wolfMonster(role: WolfRole, id: string, encounterId: string, spawn: Point): EnemyDefinition {
  const block = BLOCKS[role], visual = wolfTemplate(), appearance = structuredClone(visual.appearance)!;
  appearance.height = WOLF_VISUAL_HEIGHT * block.size;
  if (block.coat !== undefined) appearance.coat = block.coat;
  if (block.eyes !== undefined) appearance.eyes = block.eyes;
  return { id, encounterId, name: block.name, factionId: 'wolves', family: 'wolf', kind: 'monster', level: block.level, behavior: 'melee',
    color: visual.color, radius: block.radius, appearance, spawn: { ...spawn }, speed: speedForMovementDistance(WALK_SPEED, 15),
    profile: { maxHp: block.maxHp, maxWounds: 0, armor: 0, defense: 0, movementDistance: 15,
      attributes: { strength: 0, dexterity: 0, intelligence: 0, will: 0 }, attacks: structuredClone(block.attacks), traits: structuredClone(block.traits) } };
}

/** Regular Forest Wolf. */
export const wolf = (id: string, encounterId: string, spawn: Point): EnemyDefinition => wolfMonster('wolf', id, encounterId, spawn);

/** Fixed, loose pack formation; no player-count scaling. */
export function wolfPack(id: string, center: Point, count = 5): EnemyDefinition[] {
  if (!Number.isInteger(count) || count < 1) throw new RangeError('A pack needs at least one wolf.');
  return Array.from({ length: count }, (_, i) => {
    const angle = i / count * Math.PI * 2;
    return wolf(`${id}-${i}`, id, { x: center.x + Math.cos(angle) * 3, z: center.z + Math.sin(angle) * 3 });
  });
}
